// Typed client for the GoonGallery worker API: branding, tags, slogans,
// scores, leaderboard, identity, and the admin CRUD surface.
import { apiGet, apiSend, apiHref } from './client'
import { ensureNsfwTags } from '../lib/nsfwTags'
import type {
  AdminPlayer,
  Branding,
  BskySession,
  MeResponse,
  MonthlyWinner,
  RoundResult,
  ScoreEntry,
  Slogan,
  Tag,
} from '../types'

// ── public reads ──
export const getBranding = () =>
  apiGet<{ branding: Branding }>('/api/gg/branding').then((r) => r.branding)

export const getTags = () =>
  apiGet<{ tags: { id: string; label: string; query: string }[] }>('/api/gg/tags').then((r) =>
    ensureNsfwTags(r.tags.map((t) => ({ ...t, enabled: true, sort: 0 }))),
  )

export const getSlogans = () =>
  apiGet<{ slogans: Slogan[] }>('/api/gg/slogans').then((r) => r.slogans)

export const getLeaderboard = (range: 'all' | 'month') =>
  apiGet<{ entries: ScoreEntry[]; range: string }>(`/api/gg/leaderboard?range=${range}`)

export const getWinner = () =>
  apiGet<{ winner: MonthlyWinner | null }>('/api/gg/winner').then((r) => r.winner)

// ── identity ──
export const getMe = (token?: string | null) => apiGet<MeResponse>('/api/me', token)

// ── authed gameplay ──
/** Pending multiplier for the next banked round (visit increments on score bank). */
export const bumpVisit = (token: string) =>
  apiSend<{ visits: number; multiplier: number }>('/api/gg/visit', { token })

export const startRound = (token: string) =>
  apiSend<{ roundId: string; multiplier: number }>('/api/gg/round/start', { token })

export const getStanding = (token: string) =>
  apiGet<{ standing: { score: number; plays: number; rank: number } | null }>(
    '/api/gg/standing',
    token,
  ).then((r) => r.standing)

/** The player's chosen goon name (null until they pick one). */
export const getProfile = (token: string) =>
  apiGet<{ name: string | null; isAdmin: boolean }>('/api/gg/profile', token)

/** Set the goon name. Throws with the server's validation message on reject. */
export const setName = (token: string, name: string) =>
  apiSend<{ ok: boolean; name: string }>('/api/gg/name', { token, body: { name } })

export const submitScore = (token: string, r: RoundResult) =>
  apiSend<{
    id: string
    finalScore: number
    multiplier: number
    verifiedRaw: number
    visits: number
  }>('/api/gg/scores', {
    token,
    body: {
      roundId: r.roundId,
      rawScore: r.score,
      hits: r.hits,
      durationMs: r.durationMs,
      bestCombo: r.bestCombo,
      tags: r.tags,
    },
  })

// ── auth flows ──
/** App-password Bluesky login → a GoonGallery session token AND the raw
 *  Bluesky session (kept client-side to pull content through the player's
 *  own account). */
export async function blueskyLogin(
  service: string,
  identifier: string,
  password: string,
): Promise<{ token: string; user: { handle: string | null }; session: BskySession }> {
  const sess = await apiSend<{
    did: string
    handle: string
    accessJwt: string
    refreshJwt: string
  }>('/api/auth/login', { body: { service, identifier, password } })
  const res = await apiSend<{ token: string; user: { handle: string | null } }>(
    '/api/auth/bluesky',
    { body: { service, accessJwt: sess.accessJwt } },
  )
  return {
    token: res.token,
    user: res.user,
    session: {
      service,
      did: sess.did,
      handle: sess.handle,
      accessJwt: sess.accessJwt,
      refreshJwt: sess.refreshJwt,
    },
  }
}

/** Full-navigation URL to start an OAuth provider (Google). */
export const oauthStartHref = (provider: 'google') => apiHref(`/api/auth/${provider}/start`)

export const logout = (token: string) =>
  apiSend<{ ok: boolean }>('/api/auth/logout', { token }).catch(() => ({ ok: true }))

// ── admin ──
interface AdminStateRaw {
  branding: Branding
  tags: { id: string; label: string; query: string; enabled: number; sort: number }[]
  slogans: { id: string; text: string; kind: string; weight: number; enabled: number }[]
}

export async function getAdminState(
  token: string,
): Promise<{ branding: Branding; tags: Tag[]; slogans: Slogan[] }> {
  const r = await apiGet<AdminStateRaw>('/api/gg/admin/state', token)
  return {
    branding: r.branding,
    tags: r.tags.map((t) => ({ ...t, enabled: !!t.enabled })),
    slogans: r.slogans.map((s) => ({
      ...s,
      kind: s.kind as Slogan['kind'],
      enabled: !!s.enabled,
    })),
  }
}

export const putBranding = (token: string, b: Branding) =>
  apiSend<{ ok: boolean; branding: Branding }>('/api/gg/admin/branding', {
    method: 'PUT',
    token,
    body: b,
  })

export const upsertTag = (token: string, t: Partial<Tag> & { label: string; query: string }) =>
  apiSend<{ ok: boolean; tag: Tag }>('/api/gg/admin/tags', { token, body: t })

export const deleteTag = (token: string, id: string) =>
  apiSend<{ ok: boolean }>(`/api/gg/admin/tags/${id}`, { method: 'DELETE', token })

export const upsertSlogan = (
  token: string,
  s: Partial<Slogan> & { text: string; kind: string },
) => apiSend<{ ok: boolean; slogan: Slogan }>('/api/gg/admin/slogans', { token, body: s })

export const deleteSlogan = (token: string, id: string) =>
  apiSend<{ ok: boolean }>(`/api/gg/admin/slogans/${id}`, { method: 'DELETE', token })

export const runMonthly = (token: string, month?: string) =>
  apiSend<{ ran: boolean; month: string; reason?: string }>(
    `/api/gg/admin/run-monthly${month ? `?month=${month}` : ''}`,
    { token },
  )

/** Admin: who signed up + their visits/scores. */
export const getAdminPlayers = (token: string) =>
  apiGet<{ players: AdminPlayer[] }>('/api/gg/admin/players', token).then((r) => r.players)
